import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useBackofficeTenants } from '@/hooks/backoffice/useBackofficeTenants'
import { DataTable, type Column } from '@/components/ui/data-table'
import { SearchInput } from '@/components/ui/search-input'
import { Pagination } from '@/components/ui/pagination'
import { StatusBadge } from '@/components/ui/status-badge'
import { Button } from '@/components/ui/button'
import type { BackofficeTenantListItem } from '@/types/backoffice'

const COLUMNS: Column<BackofficeTenantListItem>[] = [
  { key: 'name', header: 'Razão / Nome' },
  { key: 'cnpj', header: 'CNPJ', render: (r) => r.cnpj },
  {
    key: 'userCount',
    header: 'Usuários',
    className: 'hidden sm:table-cell',
    render: (r) => r.userCount,
  },
  {
    key: 'active',
    header: 'Status',
    render: (r) => <StatusBadge active={r.active} />,
  },
]

type ActiveFilter = 'all' | 'active' | 'inactive'
const FILTER_LABELS: Record<ActiveFilter, string> = { all: 'Todos', active: 'Ativos', inactive: 'Inativos' }

export function TenantsListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [page, setPage] = useState(1)

  const activeParam =
    activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined

  const { data, isLoading } = useBackofficeTenants({ search, active: activeParam, page })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Transportadoras</h1>
          <p className="text-sm text-muted-foreground">Gerencie todos os tenants da plataforma.</p>
        </div>
        <Button onClick={() => navigate('/backoffice/tenants/new')}>
          <Plus size={16} />
          Nova transportadora
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Buscar por nome ou CNPJ…"
          className="flex-1"
        />
        <div className="flex overflow-hidden rounded-md border border-input text-sm">
          {(Object.keys(FILTER_LABELS) as ActiveFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => {
                setActiveFilter(f)
                setPage(1)
              }}
              className={`px-3 py-1.5 transition-colors ${
                activeFilter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(r) => r.id}
        onRowClick={(r) => navigate(`/backoffice/tenants/${r.id}`)}
        isLoading={isLoading}
        emptyTitle="Nenhuma transportadora cadastrada"
        emptyDescription="Crie a primeira transportadora pelo botão acima."
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}
    </div>
  )
}
